import { ChangeEvent } from 'react';
import { Flex, Group, Stack, Switch, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import classes from './NotificationSettings.module.css';
import Icon from '@/components/Icon';
import SubSectionWrapper from '@/components/layouts/AppShell/PatientProfileDrawer/SubSectionWrapper/SubSectionWrapper';
import useAuthenticatedQuery from '@/hooks/useAuthenticatedQuery';
import useWhoAmI from '@/hooks/useWhoAmI';
import {
  CommunicationOptPreference,
  ContactPreference,
} from '@/lib/graphql/types';
import {
  useNotificationProfileQuery,
  useUpsertNotificationProfileMutation,
} from '@/lib/graphql';

interface NotificationSettingsProps {
  onGoBack: () => void;
}

export const NotificationSettings = ({
  onGoBack,
}: NotificationSettingsProps) => {
  const { user } = useWhoAmI();
  const { contactPreference } = user ?? {};

  const { data: notificationProfile } = useAuthenticatedQuery(
    useNotificationProfileQuery,
    {
      skip: !user?.id,
      variables: {
        patientId: user?.id!,
      },
    }
  );

  const [updateNotificationProfile, { loading }] =
    useUpsertNotificationProfileMutation({
      awaitRefetchQueries: true,
      refetchQueries: ['NotificationProfile'],
      onCompleted: () => {
        notifications.show({
          id: 'sucess-toggle',
          color: 'green',
          message: 'Your changes have been saved!',
          icon: (
            <Icon
              name="feather/check"
              variant="filled"
              meaning="positive"
              size="sm"
            />
          ),
        });
      },
      onError: () => {
        notifications.show({
          id: 'sucess-toggle',
          color: 'red',
          message: 'Something went wrong, please try again later.',
          icon: (
            <Icon
              name="feather/x"
              variant="filled"
              meaning="negative"
              size="sm"
            />
          ),
        });
      },
    });

  const handleEmailSwitchOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (
      !user?.id ||
      !notificationProfile?.patientNotificationProfile
        .healthCommunicationsOptPreference
    ) {
      return;
    }

    updateNotificationProfile({
      variables: {
        input: {
          patientId: user.id,
          healthCommunicationsOptPreference: {
            ...notificationProfile.patientNotificationProfile
              .healthCommunicationsOptPreference,
            email: e.target.checked
              ? CommunicationOptPreference.OptedIn
              : CommunicationOptPreference.OptedOut,
          },
        },
      },
    });
  };

  const handleSmsSwitchOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (
      !user?.id ||
      !notificationProfile?.patientNotificationProfile
        .healthCommunicationsOptPreference
    ) {
      return;
    }

    updateNotificationProfile({
      variables: {
        input: {
          patientId: user.id,
          healthCommunicationsOptPreference: {
            ...notificationProfile.patientNotificationProfile
              .healthCommunicationsOptPreference,
            sms: e.target.checked
              ? CommunicationOptPreference.OptedIn
              : CommunicationOptPreference.OptedOut,
          },
        },
      },
    });
  };

  return (
    <SubSectionWrapper title="Notifications" onGoBack={onGoBack}>
      <h4 className={classes.subtitle}>
        Allow notifications to keep you on track with your health and ensure
        you&apos;re aware of important care updates.
      </h4>

      <Stack gap="24px" mt="16px">
        <Group justify="space-between" align="center">
          <span>Email</span>
          <Tooltip
            events={{
              focus: false,
              hover: contactPreference === ContactPreference.Sms,
              touch: true,
            }}
            label="At this time, your notification channel is locked and cannot be changed."
            multiline
            withArrow
            withinPortal={false}>
            <Flex gap="8px">
              <label
                aria-label="Email notifications status"
                className={classes.notificationStatusText}
                htmlFor="notificationSettings.emailNotificationSwitch">
                {notificationProfile?.patientNotificationProfile
                  .healthCommunicationsOptPreference?.email ===
                CommunicationOptPreference.OptedIn
                  ? 'On'
                  : 'Off'}
              </label>
              <Switch
                id="notificationSettings.emailNotificationSwitch"
                disabled={
                  contactPreference !== ContactPreference.Email || loading
                }
                checked={
                  notificationProfile?.patientNotificationProfile
                    .healthCommunicationsOptPreference?.email ===
                  CommunicationOptPreference.OptedIn
                }
                onChange={handleEmailSwitchOnChange}
                aria-label="Toggle email notification"
              />
            </Flex>
          </Tooltip>
        </Group>

        <Group justify="space-between" align="center">
          <span>SMS</span>
          <Tooltip
            events={{
              focus: false,
              hover: contactPreference === ContactPreference.Email,
              touch: true,
            }}
            label="At this time, your notification channel is locked and cannot be changed."
            multiline
            withArrow
            withinPortal={false}>
            <Flex gap="8px">
              <label
                aria-label="SMS notifications status"
                className={classes.notificationStatusText}
                htmlFor="notificationSettings.smsNotificationSwitch">
                {notificationProfile?.patientNotificationProfile
                  .healthCommunicationsOptPreference?.sms ===
                CommunicationOptPreference.OptedIn
                  ? 'On'
                  : 'Off'}
              </label>
              <Switch
                checked={
                  notificationProfile?.patientNotificationProfile
                    .healthCommunicationsOptPreference?.sms ===
                  CommunicationOptPreference.OptedIn
                }
                disabled={
                  contactPreference !== ContactPreference.Sms || loading
                }
                id="notificationSettings.smsNotificationSwitch"
                onChange={handleSmsSwitchOnChange}
                aria-label="Toggle SMS notification"
              />
            </Flex>
          </Tooltip>
        </Group>
      </Stack>
    </SubSectionWrapper>
  );
};
